import React from "react";
import { AlertTriangle, ArrowDownLeft, ArrowRight, ArrowUpRight, Banknote, History, Lock, ShieldCheck, Wallet } from "lucide-react";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function DualCashWidget({ cashReconciliation, onOpenCountHistory }) {
  if (!cashReconciliation) return null;

  const {
    expected_in_drawer = 0,
    expected_in_safe = 0,
    total_physical_cash = 0,
    unresolved_reopened_cash = 0,
    till_breakdown = {},
    safe_breakdown = {},
  } = cashReconciliation;

  return (
    <section className="rounded-lg border bg-card p-loose space-y-base" data-testid="dash-dual-cash">
      <div className="flex flex-wrap items-center justify-between gap-snug border-b pb-snug">
        <div className="flex items-center gap-snug">
          <Wallet className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-lede font-semibold">Dual Cash Accounting: Till Drawer & Back-Office Safe</h2>
            <p className="text-meta text-muted-foreground">
              Physical cash conservation: isolated till-to-safe transfers, debt recoveries, and intraday count tracking
            </p>
          </div>
        </div>

        <div className="flex items-center gap-base">
          <div className="text-right">
            <span className="text-[11px] uppercase font-semibold text-muted-foreground block">
              Total Physical Cash
            </span>
            <span className="text-title font-bold tabular-nums text-foreground">
              {inr(total_physical_cash)}
            </span>
          </div>

          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-tight text-meta"
            onClick={onOpenCountHistory}
          >
            <History className="h-3.5 w-3.5" />
            <span>Count History</span>
          </Button>
        </div>
      </div>

      {/* Unresolved Reopened Cash Warning Banner */}
      {unresolved_reopened_cash > 0 && (
        <div className="flex items-center gap-snug rounded-md border border-amber-500/40 bg-amber-500/10 p-snug text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div className="text-meta">
            <span className="font-semibold">{inr(unresolved_reopened_cash)}</span> physical cash is currently held from reopened orders pending bill re-closure. This cash remains conserved in expected drawer totals until re-settled or refunded.
          </div>
        </div>
      )}

      {/* Till vs Safe Columns */}
      <div className="grid gap-base md:grid-cols-2">
        {/* Till Drawer Box */}
        <div className="rounded-md border p-base space-y-snug">
          <div className="flex items-center justify-between border-b pb-tight">
            <div className="flex items-center gap-tight">
              <Banknote className="h-4 w-4 text-primary" />
              <span className="font-semibold text-body">Till Drawer</span>
            </div>
            <span className="text-lede font-bold tabular-nums text-foreground" data-testid="till-drawer-balance">
              {inr(expected_in_drawer)}
            </span>
          </div>

          <div className="space-y-hair text-meta text-muted-foreground">
            <div className="flex justify-between">
              <span>Opening Float:</span>
              <span className="tabular-nums font-medium text-foreground">{inr(till_breakdown.opening_float || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Cash Sales:</span>
              <span className="tabular-nums font-medium text-success">+{inr(till_breakdown.cash_sales || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Khata Debt Recoveries:</span>
              <span className="tabular-nums font-medium text-success">+{inr(till_breakdown.khata_debt_recoveries || 0)}</span>
            </div>
            {till_breakdown.cash_tips > 0 && (
              <div className="flex justify-between">
                <span>Cash Tips in Drawer:</span>
                <span className="tabular-nums font-medium text-foreground">+{inr(till_breakdown.cash_tips)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Drawer Top-ups (Inflows):</span>
              <span className="tabular-nums font-medium text-foreground">+{inr(till_breakdown.drawer_inflows || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Drawer Expenses & Outflows:</span>
              <span className="tabular-nums font-medium text-danger">-{inr(till_breakdown.drawer_outflows || 0)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Till Drops to Safe:</span>
              <span className="tabular-nums text-primary">-{inr(till_breakdown.till_drops_to_safe || 0)}</span>
            </div>
          </div>
        </div>

        {/* Safe Box */}
        <div className="rounded-md border p-base space-y-snug">
          <div className="flex items-center justify-between border-b pb-tight">
            <div className="flex items-center gap-tight">
              <Lock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="font-semibold text-body">Back-Office Safe</span>
            </div>
            <span className="text-lede font-bold tabular-nums text-foreground" data-testid="safe-balance">
              {inr(expected_in_safe)}
            </span>
          </div>

          <div className="space-y-hair text-meta text-muted-foreground">
            <div className="flex justify-between">
              <span>Opening Safe:</span>
              <span className="tabular-nums font-medium text-foreground">{inr(safe_breakdown.opening_safe || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Transfers Received from Till:</span>
              <span className="tabular-nums font-medium text-success">+{inr(safe_breakdown.till_drops_received || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Safe-to-Till Replenishments:</span>
              <span className="tabular-nums font-medium text-danger">-{inr(safe_breakdown.safe_to_till_replenishments || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Bank Deposits:</span>
              <span className="tabular-nums font-medium text-foreground">-{inr(safe_breakdown.bank_deposits || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Owner Withdrawals:</span>
              <span className="tabular-nums font-medium text-foreground">-{inr(safe_breakdown.owner_withdrawals || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
